import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  BookOpen,
  Clock,
  Download,
  FileText,
  CalendarClock,
  History,
  KeyRound,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Lightbulb,
  LineChart,
  ListTodo,
  Megaphone,
  MessageCircle,
  Mountain,
  Sparkles,
  Package,
  Plug,
  Plus,
  Printer,
  Search,
  Truck,
  UserPlus,
  Users,
} from 'lucide-react';

import { getShipedgeReportsStatus } from '@/api/shipedgeReports.api';
import { downloadAuditBinder } from '@/api/sops.api';
import { NAV_ITEMS } from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';
import { hasCapability } from '@/lib/capabilities';

/**
 * Cmd+K palette. Universal entry point for navigation, actions, and (in later
 * phases) AI queries. Phase 1 ships only navigation commands — subsequent
 * phases register their own commands here as features land.
 *
 * Open mechanisms:
 *   - Cmd/Ctrl + K keyboard shortcut
 *   - Custom event 'cmdk:open' (Sidebar/Header search buttons dispatch this)
 */
// Roles that can see compliance commands. Mirrors the server-side
// `requireComplianceRole(...)` allow-list. Employees still see the
// palette but get no compliance entries.
const COMPLIANCE_ROLES = new Set([
  'visionary',
  'integrator',
  'quality_lead',
  'qa_personnel',
  'auditor_readonly',
]);

export function CommandK() {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const { state: authState } = useAuth();

  const showCompliance = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return COMPLIANCE_ROLES.has(authState.me.membership.role);
  }, [authState]);

  // Directory mutations (Add employee) gate to org admins. The list +
  // Find Employee command stays visible to anyone signed in — directory
  // reads are open to every authenticated org member.
  const canManageDirectory = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return ['visionary', 'integrator'].includes(authState.me.membership.role);
  }, [authState]);

  // Insights dashboard (ADR-033) — leadership surface (dept leads get their
  // department roll-up). Matches the nav item's role gate.
  const canViewInsights = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return ['visionary', 'integrator', 'department_lead'].includes(authState.me.membership.role);
  }, [authState]);

  // The Kaerwell-specific fulfillment reports are org-gated (only the one
  // allowlisted org). Only fetch the flag for admins, and only surface those
  // commands when this org actually has the surface — so a generic tenant's
  // palette never shows fulfillment-report commands.
  const { data: fulfillmentReports } = useQuery({
    queryKey: ['shipedge-reports', 'status'],
    queryFn: getShipedgeReportsStatus,
    enabled: canManageDirectory,
    staleTime: 5 * 60 * 1000,
  });
  const showFulfillmentReports = canManageDirectory && fulfillmentReports?.enabled === true;

  const showCsWorkspace = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return hasCapability(authState.me, 'use_cs');
  }, [authState]);

  // Announcement composer gate mirrors the server: manage_announcements
  // (superadmins implicit) OR a department lead posting to their dept.
  const canPostAnnouncement = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return (
      hasCapability(authState.me, 'manage_announcements') ||
      authState.me.membership.role === 'department_lead'
    );
  }, [authState]);

  const showCsOperations = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return hasCapability(authState.me, 'view_cs_operations');
  }, [authState]);

  async function handleDownloadBinder(): Promise<void> {
    setDownloading(true);
    try {
      // Default scope (kwf, last 365 days) — same as the audit-index page.
      // The server's audit row captures the actual scope/period anyway.
      await downloadAuditBinder();
    } finally {
      setDownloading(false);
      setOpen(false);
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const handleOpenEvent = () => {
      setOpen(true);
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('cmdk:open', handleOpenEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('cmdk:open', handleOpenEvent);
    };
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close palette"
        onClick={() => {
          setOpen(false);
        }}
        className="absolute inset-0 animate-fade-in bg-black/40"
      />
      <Command
        label="Command palette"
        className="relative mx-4 w-full max-w-xl animate-slide-up overflow-hidden rounded-kw-xl bg-white shadow-kw-lg"
        loop
      >
        <div className="flex items-center gap-2 border-b border-kw-border px-4">
          <Search className="size-4 text-kw-muted" />
          <Command.Input
            autoFocus
            placeholder="Search or run a command…"
            className="flex-1 bg-transparent py-3.5 text-[14px] text-kw-shark outline-none placeholder:text-kw-muted"
          />
          <kbd className="rounded bg-kw-bg px-1.5 py-0.5 font-mono text-[11px] text-kw-muted">esc</kbd>
        </div>

        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          <Command.Empty className="py-8 text-center text-[13px] text-kw-muted">No results.</Command.Empty>

          <Command.Group
            heading="Navigate"
            className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
          >
            {NAV_ITEMS.map((item) => (
              <Command.Item
                key={item.id}
                value={`navigate ${item.label}`}
                onSelect={() => {
                  void navigate(item.href);
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <item.icon className="size-[18px] text-kw-muted" />
                <span className="flex-1">Go to {item.label}</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
            ))}
          </Command.Group>

          {/* Compliance commands. Hidden for plain employees so the
              palette doesn't tease features they can't reach. */}
          {showCompliance ? (
            <Command.Group
              heading="Compliance"
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
            >
              <Command.Item
                value="compliance sops list"
                onSelect={() => {
                  void navigate('/compliance/sops');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <FileText className="size-[18px] text-kw-muted" />
                <span className="flex-1">All SOPs</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
              <Command.Item
                value="compliance master index audit"
                onSelect={() => {
                  void navigate('/compliance/audit-index');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <BookOpen className="size-[18px] text-kw-muted" />
                <span className="flex-1">SOP master index (audit-friendly)</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
              <Command.Item
                value="compliance audit log trail history"
                onSelect={() => {
                  void navigate('/compliance/audit-log');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <History className="size-[18px] text-kw-muted" />
                <span className="flex-1">Audit log</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
              <Command.Item
                value="compliance auditor seats inspector access"
                onSelect={() => {
                  void navigate('/compliance/auditor-seats');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <KeyRound className="size-[18px] text-kw-muted" />
                <span className="flex-1">Auditor seats</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
              <Command.Item
                value="compliance download audit binder export zip"
                disabled={downloading}
                onSelect={() => {
                  void handleDownloadBinder();
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg data-[disabled='true']:cursor-default data-[disabled='true']:text-kw-muted"
              >
                <Download className="size-[18px] text-kw-muted" />
                <span className="flex-1">{downloading ? 'Building binder…' : 'Download audit binder'}</span>
              </Command.Item>
              <Command.Item
                value="compliance print this page"
                onSelect={() => {
                  setOpen(false);
                  // Defer so the palette unmounts before the browser
                  // print dialog opens — otherwise the palette captures
                  // the print preview as part of the page.
                  setTimeout(() => {
                    window.print();
                  }, 50);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Printer className="size-[18px] text-kw-muted" />
                <span className="flex-1">Print this page</span>
              </Command.Item>
            </Command.Group>
          ) : null}

          {/* Directory commands. Reads are open to any signed-in org
              member; "Add employee" gates to admins to match the
              server's `requireRole('visionary', 'integrator')`. */}
          <Command.Group
            heading="Directory"
            className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
          >
            <Command.Item
              value="directory employees find people"
              onSelect={() => {
                void navigate('/employees');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <Users className="size-[18px] text-kw-muted" />
              <span className="flex-1">Find employee</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            {canManageDirectory ? (
              <Command.Item
                value="directory add employee new hire"
                onSelect={() => {
                  // Land on the page; the user can click "Add employee"
                  // — opening the modal directly via Cmd+K is a future
                  // wire-up via a custom event, like the search button.
                  void navigate('/employees');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <UserPlus className="size-[18px] text-kw-muted" />
                <span className="flex-1">Add employee</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
            ) : null}
            {canManageDirectory ? (
              <Command.Item
                value="directory departments admin manage"
                onSelect={() => {
                  void navigate('/settings/departments');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Layers className="size-[18px] text-kw-muted" />
                <span className="flex-1">Manage departments</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
            ) : null}
          </Command.Group>

          {/* EOS — open to every org member (Stream B Wave 1). */}
          <Command.Group
            heading="EOS"
            className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
          >
            <Command.Item
              value="rocks quarterly goals eos"
              onSelect={() => {
                void navigate('/eos/rocks');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <Mountain className="size-[18px] text-kw-muted" />
              <span className="flex-1">Rocks</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            <Command.Item
              value="todos to-dos tasks eos"
              onSelect={() => {
                void navigate('/eos/todos');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <ListTodo className="size-[18px] text-kw-muted" />
              <span className="flex-1">To-Dos</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            <Command.Item
              value="issues ids list eos"
              onSelect={() => {
                void navigate('/eos/issues');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <Lightbulb className="size-[18px] text-kw-muted" />
              <span className="flex-1">Issues</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            <Command.Item
              value="l10 meetings level ten eos"
              onSelect={() => {
                void navigate('/eos/meetings');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <CalendarClock className="size-[18px] text-kw-muted" />
              <span className="flex-1">L10 Meetings</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            <Command.Item
              value="my day dashboard home"
              onSelect={() => {
                void navigate('/dashboard');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <LayoutDashboard className="size-[18px] text-kw-muted" />
              <span className="flex-1">My Day</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
          </Command.Group>

          {/* Communication (Stream E). Messages + shout-outs are open to
              every member; the announcement composer is gated. */}
          <Command.Group
            heading="Communication"
            className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
          >
            <Command.Item
              value="messages chat dm direct message conversations"
              onSelect={() => {
                void navigate('/messages');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <MessageCircle className="size-[18px] text-kw-muted" />
              <span className="flex-1">Messages</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            <Command.Item
              value="new message chat compose dm"
              onSelect={() => {
                void navigate('/messages?new=1');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <Plus className="size-[18px] text-kw-muted" />
              <span className="flex-1">New message</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            <Command.Item
              value="communication announcements shout-outs feed"
              onSelect={() => {
                void navigate('/communication');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <Megaphone className="size-[18px] text-kw-muted" />
              <span className="flex-1">Communication</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            <Command.Item
              value="give shout-out recognition core value praise"
              onSelect={() => {
                void navigate('/communication?compose=shoutout');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <Sparkles className="size-[18px] text-kw-muted" />
              <span className="flex-1">Give a shout-out</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            {canPostAnnouncement ? (
              <Command.Item
                value="new announcement post publish broadcast"
                onSelect={() => {
                  void navigate('/communication?compose=announcement');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Plus className="size-[18px] text-kw-muted" />
                <span className="flex-1">New announcement</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
            ) : null}
          </Command.Group>

          {/* Scorecards. Read access for any signed-in org member;
              "New metric" gates to admins same as the page. */}
          <Command.Group
            heading="Scorecards"
            className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
          >
            <Command.Item
              value="scorecards open grid metrics"
              onSelect={() => {
                void navigate('/scorecards');
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
            >
              <BarChart3 className="size-[18px] text-kw-muted" />
              <span className="flex-1">Open scorecard</span>
              <ArrowRight className="size-4 text-kw-muted" />
            </Command.Item>
            {canManageDirectory ? (
              <Command.Item
                value="scorecards new metric add"
                onSelect={() => {
                  // Land on /scorecards; admin clicks "Add metric"
                  // there. Direct-open-modal via custom event is a
                  // future polish.
                  void navigate('/scorecards');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Plus className="size-[18px] text-kw-muted" />
                <span className="flex-1">New metric</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
            ) : null}
            {canManageDirectory ? (
              <Command.Item
                value="scorecards integrations connect provider shipedge"
                onSelect={() => {
                  void navigate('/settings/integrations');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Plug className="size-[18px] text-kw-muted" />
                <span className="flex-1">Connect integration</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
            ) : null}
          </Command.Group>

          {/* Customer Service (Phase 16). Grant-based: `use_cs` for the
              workspace, `view_cs_operations` for the ops surfaces —
              same gates as the nav + server routes. */}
          {showCsWorkspace || showCsOperations ? (
            <Command.Group
              heading="Customer Service"
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
            >
              {showCsWorkspace ? (
                <Command.Item
                  value="customer service cs tickets queue support"
                  onSelect={() => {
                    void navigate('/cs');
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
                >
                  <LifeBuoy className="size-[18px] text-kw-muted" />
                  <span className="flex-1">Ticket queue</span>
                  <ArrowRight className="size-4 text-kw-muted" />
                </Command.Item>
              ) : null}
              {showCsOperations ? (
                <Command.Item
                  value="customer service cs operations health routing agents"
                  onSelect={() => {
                    void navigate('/cs/operations');
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
                >
                  <Activity className="size-[18px] text-kw-muted" />
                  <span className="flex-1">CS Operations</span>
                  <ArrowRight className="size-4 text-kw-muted" />
                </Command.Item>
              ) : null}
            </Command.Group>
          ) : null}

          {/* Alerts (Phase 14). Day 1 ships only the Capabilities tab,
              gated to superadmins same as the page itself. The
              Subscriptions / Dispatches commands land alongside their
              tabs in subsequent days, with capability-based gating
              for non-superadmin holders. */}
          {canManageDirectory ? (
            <Command.Group
              heading="Alerts"
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
            >
              <Command.Item
                value="alerts settings capabilities admin manage"
                onSelect={() => {
                  void navigate('/settings/alerts');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Bell className="size-[18px] text-kw-muted" />
                <span className="flex-1">Alert settings</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
              <Command.Item
                value="alerts dispatches sent emails history"
                onSelect={() => {
                  void navigate('/settings/alerts');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Bell className="size-[18px] text-kw-muted" />
                <span className="flex-1">Alert dispatches</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
              <Command.Item
                value="overtime tracker hours ledger weekly"
                onSelect={() => {
                  void navigate('/admin/overtime');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Clock className="size-[18px] text-kw-muted" />
                <span className="flex-1">Overtime tracker</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
              <Command.Item
                value="overtime upload hours time data gusto"
                onSelect={() => {
                  void navigate('/admin/overtime');
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
              >
                <Clock className="size-[18px] text-kw-muted" />
                <span className="flex-1">Upload time data</span>
                <ArrowRight className="size-4 text-kw-muted" />
              </Command.Item>
            </Command.Group>
          ) : null}

          {/* Reports & insights (ADR-033). The generic KPI dashboard is the
              primary, industry-neutral surface (leadership). The fulfillment
              reports below are Kaerwell-specific + org-gated, so they only
              appear for the one allowlisted org — never in a generic tenant's
              palette. */}
          {canViewInsights || showFulfillmentReports ? (
            <Command.Group
              heading="Reports & insights"
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-kw-muted"
            >
              {canViewInsights ? (
                <Command.Item
                  value="insights dashboard kpi metrics scorecard trends analytics reports"
                  onSelect={() => {
                    void navigate('/insights');
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
                >
                  <LineChart className="size-[18px] text-kw-muted" />
                  <span className="flex-1">Insights dashboard</span>
                  <ArrowRight className="size-4 text-kw-muted" />
                </Command.Item>
              ) : null}
              {showFulfillmentReports ? (
                <>
                  <Command.Item
                    value="fulfillment customer profitability dashboard p&l shipedge"
                    onSelect={() => {
                      void navigate('/admin/shipedge/customer-profitability');
                      setOpen(false);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
                  >
                    <BarChart3 className="size-[18px] text-kw-muted" />
                    <span className="flex-1">Customer profitability</span>
                    <ArrowRight className="size-4 text-kw-muted" />
                  </Command.Item>
                  <Command.Item
                    value="fulfillment operational mix pick type carrier shipedge"
                    onSelect={() => {
                      void navigate('/admin/shipedge/operational-mix');
                      setOpen(false);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
                  >
                    <Truck className="size-[18px] text-kw-muted" />
                    <span className="flex-1">Operational mix</span>
                    <ArrowRight className="size-4 text-kw-muted" />
                  </Command.Item>
                  <Command.Item
                    value="fulfillment lot lookup sku expiration inventory shipedge"
                    onSelect={() => {
                      void navigate('/admin/shipedge/lot-lookup');
                      setOpen(false);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
                  >
                    <Package className="size-[18px] text-kw-muted" />
                    <span className="flex-1">Lot lookup</span>
                    <ArrowRight className="size-4 text-kw-muted" />
                  </Command.Item>
                  <Command.Item
                    value="fulfillment sync log run history shipedge"
                    onSelect={() => {
                      void navigate('/admin/shipedge/sync-log');
                      setOpen(false);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] text-kw-shark aria-selected:bg-kw-bg"
                  >
                    <History className="size-[18px] text-kw-muted" />
                    <span className="flex-1">Fulfillment sync log</span>
                    <ArrowRight className="size-4 text-kw-muted" />
                  </Command.Item>
                </>
              ) : null}
            </Command.Group>
          ) : null}
        </Command.List>
      </Command>
    </div>
  );
}
